
import { useState, useEffect } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { setSentryUser, clearSentryUser } from "@/utils/sentry";

export const useAuth = () => {
  const CUSTOM_SESSION_KEY = "sb-custom-session";
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log("Auth state changed:", event, session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);
        
        // Set Sentry user context when user logs in
        if (session?.user) {
          setSentryUser(
            session.user.id,
            session.user.email,
            session.user.user_metadata?.username
          );
        } else if (event === 'SIGNED_OUT') {
          clearSentryUser();
        }
        
        try {
          if (session?.access_token && session?.refresh_token) {
            // Persist tokens on any event that includes a session
            localStorage.setItem(
              CUSTOM_SESSION_KEY,
              JSON.stringify({ access_token: session.access_token, refresh_token: session.refresh_token })
            );
          } else {
            // Only clear on explicit sign-out, not on INITIAL_SESSION
            if (event === 'SIGNED_OUT') {
              localStorage.removeItem(CUSTOM_SESSION_KEY);
            }
          }
        } catch (e) {
          console.warn("Failed to persist custom session", e);
        }
        setLoading(false);
      }
    );

    // If returning from OAuth redirect, explicitly exchange the code for a session
    const url = new URL(window.location.href);
    const hasCode = url.searchParams.get("code");
    const hasState = url.searchParams.get("state");

    const init = async () => {
      try {
        if (hasCode && hasState) {
          const { error } = await supabase.auth.exchangeCodeForSession(window.location.href);
          if (error) {
            console.error("OAuth exchange error:", error);
          } else {
            // Clean URL to avoid re-exchanging on future navigations, but preserve redirect param if present
            const redirectParam = url.searchParams.get("redirect");
            window.history.replaceState({}, document.title, url.origin + url.pathname + (redirectParam ? `?redirect=${encodeURIComponent(redirectParam)}` : "") + url.hash);
          }
        }

        // THEN check for existing session (covers both normal load and after exchange)
        const { data: { session } } = await supabase.auth.getSession();
        console.log("Initial session:", session?.user?.email);
        setSession(session);
        setUser(session?.user ?? null);

        // Fallback: restore session from custom storage if missing
        if (!session) {
          try {
            const raw = localStorage.getItem(CUSTOM_SESSION_KEY);
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed?.access_token && parsed?.refresh_token) {
                const { data, error } = await supabase.auth.setSession({
                  access_token: parsed.access_token,
                  refresh_token: parsed.refresh_token,
                });
                if (!error) {
                  setSession(data.session);
                  setUser(data.session?.user ?? null);
                }
              }
            }
          } catch (e) {
            console.warn("Failed to restore session from custom storage", e);
          }
        }
      } finally {
        setLoading(false);
      }
    };

    init();

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    try { localStorage.removeItem(CUSTOM_SESSION_KEY); } catch {}
    
    // Clear Sentry user context
    clearSentryUser();
    
    // Always clear local state first
    setUser(null);
    setSession(null);
    
    // Then try to sign out from Supabase
    const { error } = await supabase.auth.signOut();
    if (error && error.message !== "Auth session missing!") {
      console.error("Sign out error:", error);
    }
  };

  return {
    user,
    session,
    loading,
    signOut,
    isAuthenticated: !!user,
  };
};
